import type { ServicesSectionContent } from "@site-factory/config";
import { cn } from "../utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/Tabs";
import { SectionTitle, SectionSubtitle } from "../typography/Typography";
import { Card, CardContent } from "../components/Card";
import { SectionShell } from "../components/SectionShell";
import { AnimatedIcon } from "../components/AnimatedIcon";
import { Icon, iconNames, type IconName } from "../icons";

export interface ServicesSectionProps extends ServicesSectionContent {
  className?: string;
}

export function ServicesSection({
  title,
  subtitle,
  services,
  groups,
  variant = "grid",
  className,
}: ServicesSectionProps) {
  const renderServices = (items: typeof services) => (
    <div
      className={cn(
        variant === "grid"
          ? "grid gap-8 md:grid-cols-2 lg:grid-cols-3"
          : "space-y-4"
      )}
    >
      {items.map((service, index) => {
        const isIconName = service.icon && iconNames.includes(service.icon as IconName);
        return (
          <Card
            key={index}
            className="p-6 shadow-sm-2 hover:shadow-md-2 transition h-full border border-neutral-200/60 dark:border-white/10 group"
          >
            <CardContent className="p-0 space-y-3">
              <div className="flex items-center gap-3">
                {service.icon && (
                  <AnimatedIcon variant="scale" className="h-12 w-12 rounded-xl bg-primary/10 text-primary shrink-0">
                    {isIconName ? (
                      <Icon name={service.icon as IconName} className="w-6 h-6" />
                    ) : (
                      <span className="text-2xl">{service.icon}</span>
                    )}
                  </AnimatedIcon>
                )}
                <h3
                  className="text-lg font-semibold"
                  style={{ color: "var(--color-text, #0f172a)" }}
                >
                  {service.name}
                </h3>
              </div>
              <p className="text-sm" style={{ color: "var(--color-text, #0f172a)", opacity: 0.82 }}>
                {service.description}
              </p>
              {service.href && (
                <a
                  href={service.href}
                  className="inline-flex items-center gap-2 mt-2 text-primary font-semibold hover:underline"
                >
                  Learn more <span aria-hidden="true">→</span>
                </a>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  return (
    <SectionShell className={className} backgroundClassName="bg-surface-alt">
      {(title || subtitle) && (
        <div className="text-center mb-12">
          {title && <SectionTitle>{title}</SectionTitle>}
          {subtitle && <SectionSubtitle className="mt-4">{subtitle}</SectionSubtitle>}
        </div>
      )}

      {groups && groups.length > 0 ? (
        <Tabs defaultValue={groups[0]?.label}>
          <div className="flex flex-col items-center mb-6">
            <TabsList>
              {groups.map((group) => (
                <TabsTrigger key={group.label} value={group.label}>
                  {group.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {groups.map((group) => (
            <TabsContent key={group.label} value={group.label} className="bg-transparent border-none p-0 shadow-none">
              {renderServices(group.services)}
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        renderServices(services)
      )}
    </SectionShell>
  );
}
